import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  MachineGroupsState,
  ChangeMachineGroupViewModel,
} from "./models/groupModels";
import machineGroupsApi from "./api/machineGroupsApi";

// =====================================================================
//  ASYNC THUNKS
// =====================================================================

export const fetchGroups = createAsyncThunk(
  "machineGroups/fetchGroups",
  async (groupType?: string) => {
    return machineGroupsApi.getGroups(groupType);
  }
);

export const fetchGroupDetail = createAsyncThunk(
  "machineGroups/fetchGroupDetail",
  async (id: string) => {
    return machineGroupsApi.getGroupDetail(id);
  }
);

export const createGroup = createAsyncThunk(
  "machineGroups/createGroup",
  async (model: ChangeMachineGroupViewModel) => {
    return machineGroupsApi.createGroup(model);
  }
);

export const updateGroup = createAsyncThunk(
  "machineGroups/updateGroup",
  async ({ id, model }: { id: string; model: ChangeMachineGroupViewModel }) => {
    return machineGroupsApi.updateGroup(id, model);
  }
);

export const deleteGroup = createAsyncThunk(
  "machineGroups/deleteGroup",
  async (id: string) => {
    await machineGroupsApi.deleteGroup(id);
    return id;
  }
);

export const addMachinesToGroup = createAsyncThunk(
  "machineGroups/addMachinesToGroup",
  async ({ groupId, machineIds }: { groupId: string; machineIds: string[] }) => {
    return machineGroupsApi.addMachinesToGroup(groupId, { machineIds });
  }
);

export const removeMachineFromGroup = createAsyncThunk(
  "machineGroups/removeMachineFromGroup",
  async ({ groupId, machineId }: { groupId: string; machineId: string }) => {
    return machineGroupsApi.removeMachineFromGroup(groupId, machineId);
  }
);

// =====================================================================
//  SLICE
// =====================================================================

const initialState: MachineGroupsState = {
  groups: [],
  selectedGroup: null,
  loading: false,
  error: undefined,
};

const machineGroupsSlice = createSlice({
  name: "machineGroups",
  initialState,
  reducers: {
    clearSelectedGroup: (state) => {
      state.selectedGroup = null;
    },
    clearGroupsError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    // fetchGroups
    builder
      .addCase(fetchGroups.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGroups.fulfilled, (state, { payload }) => {
        state.groups = payload;
        state.loading = false;
      })
      .addCase(fetchGroups.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // fetchGroupDetail
    builder
      .addCase(fetchGroupDetail.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchGroupDetail.fulfilled, (state, { payload }) => {
        state.selectedGroup = payload;
        state.loading = false;
      })
      .addCase(fetchGroupDetail.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // createGroup
    builder
      .addCase(createGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(createGroup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(createGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // updateGroup
    builder
      .addCase(updateGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateGroup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // deleteGroup
    builder
      .addCase(deleteGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteGroup.fulfilled, (state, { payload }) => {
        state.groups = state.groups.filter((g) => g.id !== payload);
        state.loading = false;
      })
      .addCase(deleteGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // addMachinesToGroup
    builder
      .addCase(addMachinesToGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(addMachinesToGroup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(addMachinesToGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // removeMachineFromGroup
    builder
      .addCase(removeMachineFromGroup.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeMachineFromGroup.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(removeMachineFromGroup.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  },
});

export const { clearSelectedGroup, clearGroupsError } =
  machineGroupsSlice.actions;

export default machineGroupsSlice.reducer;
